import React, { useEffect, useState } from "react";
import { Box, Text, VStack, HStack, Progress, Spinner } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";

interface HotTopic {
  content_id: number;
  task_title: string;
  discussion_count: number;
  pro_count: number;
  con_count: number;
}

// Pulsing glow animation
const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 10px rgba(239, 68, 68, 0.3), 0 0 10px rgba(34, 197, 94, 0.3); }
  50% { box-shadow: 0 0 20px rgba(239, 68, 68, 0.5), 0 0 20px rgba(34, 197, 94, 0.5); }
`;

const HotTopics: React.FC = () => {
  const [topics, setTopics] = useState<HotTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchHotTopics = async () => {
      try {
        const res = await api.get("/api/discussion/hot-topics");
        const data = res.data || [];

        // If no data from API, use fallback sample data
        if (data.length === 0) {
          setTopics([
            {
              content_id: 9999,
              task_title: "Climate Change Evidence Analysis",
              discussion_count: 34,
              pro_count: 21,
              con_count: 13,
            },
            {
              content_id: 9998,
              task_title: "Social Media Impact Study",
              discussion_count: 28,
              pro_count: 15,
              con_count: 13,
            },
            {
              content_id: 9997,
              task_title: "AI Safety Research Review",
              discussion_count: 19,
              pro_count: 12,
              con_count: 7,
            },
          ]);
        } else {
          setTopics(data);
        }
      } catch (error) {
        console.error("Failed to fetch hot topics:", error);
        // On error, show fallback data
        setTopics([
          {
            content_id: 9999,
            task_title: "Climate Change Evidence Analysis",
            discussion_count: 34,
            pro_count: 21,
            con_count: 13,
          },
          {
            content_id: 9998,
            task_title: "Social Media Impact Study",
            discussion_count: 28,
            pro_count: 15,
            con_count: 13,
          },
          {
            content_id: 9997,
            task_title: "AI Safety Research Review",
            discussion_count: 19,
            pro_count: 12,
            con_count: 7,
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchHotTopics();

    // Listen for new discussion posts to refresh stats
    const handleDiscussionPosted = () => {
      console.log("🔄 [HotTopics] New discussion posted, refreshing...");
      fetchHotTopics();
    };

    window.addEventListener("discussion-posted", handleDiscussionPosted);

    return () => {
      window.removeEventListener("discussion-posted", handleDiscussionPosted);
    };
  }, []);

  const handleTopicClick = (contentId: number) => {
    // Navigate to workspace for this content
    navigate(`/workspace?content_id=${contentId}`);
  };

  if (loading) {
    return (
      <Box
        p={3}
        background="rgba(0, 0, 0, 0.4)"
        border="1px solid rgba(0, 162, 255, 0.3)"
        borderRadius="8px"
        textAlign="center" _light={{ background: "rgba(120, 96, 64, 0.1)", border: "1px solid rgba(38, 53, 85, 0.4)" }}
      >
        <Spinner size="sm" color="#00a2ff" _light={{ color: "#263555" }} />
      </Box>
    );
  }

  return (
    <Box
      position="relative"
      w="full"
      background="rgba(0, 0, 0, 0.4)"
      border="1px solid rgba(0, 162, 255, 0.3)"
      borderRadius="8px"
      p={{ base: 2, xl: 3 }}
      overflow="hidden" _light={{ background: "rgba(120, 96, 64, 0.1)", border: "1px solid rgba(38, 53, 85, 0.4)" }}
    >
      {/* Scanlines */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        background="repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 162, 255, 0.03) 2px, rgba(0, 162, 255, 0.03) 4px)"
        pointerEvents="none"
        borderRadius="8px"
        zIndex={1} _light={{ background: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 162, 255, 0.013) 2px, rgba(0, 162, 255, 0.013) 4px)" }}
      />

      <Box position="relative" zIndex={2}>
        {/* Header */}
        <Text
          color="#00a2ff"
          fontSize={{ base: "0.55rem", xl: "0.65rem" }}
          fontWeight="700"
          textTransform="uppercase"
          letterSpacing="1px"
          mb={2}
          textAlign="center"
          textShadow="0 0 8px rgba(0, 162, 255, 0.6)" _light={{ color: "#263555", textShadow: "none" }}
        >
          ⚡ Hot Topics ⚡
        </Text>

        {/* Topics List */}
        <VStack spacing={2} align="stretch">
          {topics.map((topic, index) => {
            const total = topic.discussion_count;
            const proPercent = total > 0 ? (topic.pro_count / total) * 100 : 50;
            const conPercent = total > 0 ? (topic.con_count / total) * 100 : 50;

            return (
              <Box
                key={topic.content_id}
                p={{ base: 1.5, xl: 2 }}
                background={
                  index === 0
                    ? "rgba(0, 162, 255, 0.1)"
                    : "rgba(15, 23, 42, 0.5)"
                }
                border={
                  index === 0
                    ? "1px solid rgba(0, 162, 255, 0.4)"
                    : "1px solid rgba(100, 116, 139, 0.2)"
                }
                borderRadius="6px"
                cursor="pointer"
                transition="all 0.3s ease"
                _hover={{
                  background: "rgba(0, 162, 255, 0.15)",
                  borderColor: "rgba(0, 162, 255, 0.5)",
                  transform: "translateX(2px)", _light: { background: "rgba(0, 162, 255, 0.068)", borderColor: "rgba(38, 53, 85, 0.4)" }
                }}
                onClick={() => handleTopicClick(topic.content_id)}
                sx={
                  index === 0
                    ? {
                        animation: `${pulseGlow} 3s ease-in-out infinite`,
                      }
                    : {}
                } _light={{ background: (index === 0) ? "rgba(0, 162, 255, 0.045)" : "rgba(120, 96, 64, 0.1)", border: (index === 0) ? "1px solid rgba(38, 53, 85, 0.4)" : "1px solid rgba(28, 24, 19, 0.2)" }}
              >
                <VStack spacing={{ base: 1.5, xl: 2 }} align="stretch">
                  {/* Topic Title */}
                  <HStack spacing={{ base: 1, xl: 2 }} minW={0}>
                    {/* Rank Badge */}
                    <Box
                      minW={{ base: "16px", xl: "18px" }}
                      h={{ base: "16px", xl: "18px" }}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      background={
                        index === 0
                          ? "linear-gradient(135deg, rgba(239, 68, 68, 0.3), rgba(239, 68, 68, 0.1))"
                          : "rgba(100, 116, 139, 0.2)"
                      }
                      borderRadius="3px"
                      fontSize={{ base: "0.5rem", xl: "0.6rem" }}
                      fontWeight="700"
                      color={index === 0 ? "#f87171" : "#94a3b8"}
                      flexShrink={0} _light={{ background: (index === 0) ? "linear-gradient(135deg, rgba(239, 68, 68, 0.12), rgba(239, 68, 68, 0.045))" : "rgba(120, 96, 64, 0.07)", color: (index === 0) ? "#9b1c1c" : "#5c5347" }}
                    >
                      {index + 1}
                    </Box>

                    <Text
                      fontSize={{ base: "0.6rem", xl: "0.7rem" }}
                      fontWeight="600"
                      color="#e2e8f0"
                      isTruncated
                      flex={1}
                      minW={0} _light={{ color: "#263555" }}
                    >
                      {topic.task_title}
                    </Text>

                    {/* Discussion Count Badge */}
                    <Box
                      px={{ base: 1, xl: 2 }}
                      py={0.5}
                      background="rgba(0, 162, 255, 0.2)"
                      borderRadius="3px"
                      fontSize={{ base: "0.5rem", xl: "0.6rem" }}
                      fontWeight="700"
                      color="#60a5fa"
                      flexShrink={0} _light={{ background: "rgba(0, 162, 255, 0.09)", color: "#263555" }}
                    >
                      {total}
                    </Box>
                  </HStack>

                  {/* Pro/Con Split Visualization */}
                  <VStack spacing={1} align="stretch">
                    {/* Split Bar */}
                    <HStack
                      spacing={0}
                      h={{ base: "5px", xl: "6px" }}
                      borderRadius="3px"
                      overflow="hidden"
                    >
                      <Box
                        flex={proPercent}
                        background="linear-gradient(90deg, rgba(34, 197, 94, 0.6), rgba(34, 197, 94, 0.3))"
                        boxShadow="0 0 8px rgba(34, 197, 94, 0.4)" _light={{ background: "linear-gradient(90deg, rgba(34, 197, 94, 0.12), rgba(34, 197, 94, 0.12))", boxShadow: "var(--paper-lift)" }}
                      />
                      <Box
                        flex={conPercent}
                        background="linear-gradient(90deg, rgba(239, 68, 68, 0.3), rgba(239, 68, 68, 0.6))"
                        boxShadow="0 0 8px rgba(239, 68, 68, 0.4)" _light={{ background: "linear-gradient(90deg, rgba(239, 68, 68, 0.12), rgba(239, 68, 68, 0.12))", boxShadow: "var(--paper-lift)" }}
                      />
                    </HStack>

                    {/* Legend */}
                    <HStack
                      spacing={{ base: 2, xl: 3 }}
                      justify="space-between"
                      fontSize={{ base: "0.5rem", xl: "0.55rem" }}
                    >
                      <HStack spacing={1}>
                        <Box
                          w={{ base: "3px", xl: "4px" }}
                          h={{ base: "3px", xl: "4px" }}
                          borderRadius="50%"
                          background="#4ade80"
                          boxShadow="0 0 4px rgba(34, 197, 94, 0.6)"
                          flexShrink={0} _light={{ background: "#e6f3dd", boxShadow: "var(--paper-lift)" }}
                        />
                        <Text color="#4ade80" fontWeight="600" isTruncated _light={{ color: "#2f5d2a" }}>
                          {topic.pro_count} Pro
                        </Text>
                      </HStack>
                      <HStack spacing={1}>
                        <Text color="#f87171" fontWeight="600" isTruncated _light={{ color: "#9b1c1c" }}>
                          {topic.con_count} Con
                        </Text>
                        <Box
                          w={{ base: "3px", xl: "4px" }}
                          h={{ base: "3px", xl: "4px" }}
                          borderRadius="50%"
                          background="#f87171"
                          boxShadow="0 0 4px rgba(239, 68, 68, 0.6)"
                          flexShrink={0} _light={{ background: "#fbe6db", boxShadow: "var(--paper-lift)" }}
                        />
                      </HStack>
                    </HStack>
                  </VStack>
                </VStack>
              </Box>
            );
          })}
        </VStack>
      </Box>
    </Box>
  );
};

export default HotTopics;
